import { useEffect, useRef, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { X, Send, ArrowLeft, Plus, Users, MessageSquare, ShieldAlert, Eye, Loader2 } from "lucide-react";

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

function NewChat({ onBack, onCreated }) {
  const [members, setMembers] = useState([]);
  const [mode, setMode] = useState("direct");
  const [picked, setPicked] = useState([]);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/family/members").then((r) => setMembers(r.data)).catch(() => {}); }, []);
  const toggle = (id) => setPicked((p) => p.includes(id) ? p.filter((x) => x !== id) : (mode === "direct" ? [id] : [...p, id]));
  const create = async () => {
    if (!picked.length) return toast.error("Pick someone.");
    if (mode === "group" && !title.trim()) return toast.error("Name your group.");
    setBusy(true);
    try {
      const { data } = await api.post("/conversations", { type: mode, member_ids: picked, title });
      onCreated(data.id);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-[#EAE6DF] flex items-center gap-2">
        <button data-testid="newchat-back" onClick={onBack} className="text-[#6B7280] hover:text-[#3D405B]"><ArrowLeft size={18} /></button>
        <span className="font-head font-semibold text-[#3D405B]">New message</span>
      </div>
      <div className="p-4">
        <div className="flex gap-2 mb-4">
          <button data-testid="mode-direct" onClick={() => { setMode("direct"); setPicked([]); }} className={`flex-1 rounded-full py-2 text-sm font-medium transition-colors ${mode === "direct" ? "bg-[#3D405B] text-white" : "border border-[#EAE6DF] text-[#6B7280]"}`}>Direct</button>
          <button data-testid="mode-group" onClick={() => { setMode("group"); setPicked([]); }} className={`flex-1 rounded-full py-2 text-sm font-medium transition-colors ${mode === "group" ? "bg-[#3D405B] text-white" : "border border-[#EAE6DF] text-[#6B7280]"}`}>Group</button>
        </div>
        {mode === "group" && (
          <input data-testid="group-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Group name (e.g. Whole Family)"
            className="w-full mb-3 rounded-xl border border-[#EAE6DF] px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3D405B]" />
        )}
        <p className="text-xs text-[#6B7280] mb-2">{mode === "direct" ? "Pick one person" : "Pick people to include"}</p>
        <div className="space-y-2 max-h-[45vh] overflow-y-auto">
          {members.map((m) => (
            <button key={m.id} data-testid={`pick-${m.id}`} onClick={() => toggle(m.id)}
              className={`w-full flex items-center justify-between rounded-xl border px-4 py-2.5 text-sm transition-colors ${picked.includes(m.id) ? "border-[#3D405B] bg-[#3D405B]/5" : "border-[#EAE6DF] hover:border-[#3D405B]/40"}`}>
              <span className="text-[#3D405B]">{m.first_name} {m.last_name} <span className="text-[#6B7280] text-xs">· {m.role}</span></span>
              {picked.includes(m.id) && <span className="w-2.5 h-2.5 rounded-full bg-[#3D405B]" />}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-auto p-4 border-t border-[#EAE6DF]">
        <button data-testid="create-conversation" onClick={create} disabled={busy}
          className="w-full rounded-full bg-[#E07A5F] text-white py-3 font-semibold hover:bg-[#c96851] transition-colors disabled:opacity-60">{busy ? "…" : "Start chat"}</button>
      </div>
    </div>
  );
}

function Thread({ convId, onBack, onActivity }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottom = useRef(null);
  const lastCount = useRef(0);

  const load = async () => {
    try {
      const { data } = await api.get(`/conversations/${convId}/messages`);
      setData(data);
      if (data.messages.length > lastCount.current) { bottom.current?.scrollIntoView({ behavior: "smooth" }); }
      lastCount.current = data.messages.length;
      onActivity?.();
    } catch (e) { console.debug("message poll failed:", e); }
  };
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [convId]); // eslint-disable-line

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setText(""); setSending(true);
    try { await api.post(`/conversations/${convId}/messages`, { text: t }); await load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); setText(t); }
    finally { setSending(false); }
  };

  if (!data) return <div className="p-6 text-[#6B7280] flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</div>;
  const conv = data.conversation;
  const otherId = conv.type === "direct" ? conv.member_ids.find((m) => m !== user.id) : null;

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-[#EAE6DF] flex items-center gap-2">
        <button data-testid="thread-back" onClick={onBack} className="text-[#6B7280] hover:text-[#3D405B]"><ArrowLeft size={18} /></button>
        <div className="min-w-0">
          <p className="font-head font-semibold text-[#3D405B] truncate flex items-center gap-2">
            {conv.type === "group" ? <Users size={15} /> : <MessageSquare size={15} />} {conv.name}
          </p>
          {conv.type === "group" && <p className="text-xs text-[#6B7280] truncate">{Object.values(conv.member_names).join(", ")}</p>}
        </div>
      </div>
      {!data.is_member && (
        <div className="px-4 py-2 bg-[#F4A261]/15 text-[#3D405B] text-xs flex items-center gap-1.5"><Eye size={13} /> Viewing for oversight — you're not a participant, so you can't send here.</div>
      )}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
        {data.messages.map((m) => {
          const mine = m.sender_id === user.id;
          const seen = conv.type === "direct" && otherId && (m.read_by || []).includes(otherId);
          return (
            <div key={m.id} className={mine ? "flex flex-col items-end" : "flex flex-col items-start"}>
              {conv.type === "group" && !mine && <span className="text-[11px] text-[#6B7280] mb-0.5 px-1">{m.sender_name}</span>}
              <div data-testid="chat-message" className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-[#81B29A] text-white" : "bg-white border border-[#EAE6DF] text-[#3D405B]"}`}>{m.text}</div>
              <span className="text-[10px] text-[#9CA3AF] mt-0.5 px-1">{fmtTime(m.created_at)}{mine && conv.type === "direct" ? (seen ? " · Seen" : " · Sent") : ""}</span>
            </div>
          );
        })}
        {data.messages.length === 0 && <p className="text-center text-sm text-[#6B7280] mt-6">No messages yet — say hello!</p>}
        <div ref={bottom} />
      </div>
      {data.is_member && (
        <div className="p-3 border-t border-[#EAE6DF] flex gap-2">
          <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Type a message…" className="flex-1 rounded-full border border-[#EAE6DF] px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#81B29A]" />
          <button data-testid="chat-send" onClick={send} disabled={sending} className="grid place-items-center w-11 h-11 rounded-full bg-[#81B29A] text-white hover:bg-[#6f9d87] transition-colors"><Send size={16} /></button>
        </div>
      )}
    </div>
  );
}

export function MessagesDrawer({ onClose, onUnreadChange }) {
  const [view, setView] = useState("list"); // list | new | thread
  const [convId, setConvId] = useState(null);
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadList = async () => {
    try {
      const { data } = await api.get("/conversations");
      setConvs(data.conversations);
      onUnreadChange?.(data.unread_total);
    } catch (e) { console.debug("conversation list poll failed:", e); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadList(); const t = setInterval(loadList, 6000); return () => clearInterval(t); }, []); // eslint-disable-line

  return (
    <motion.div initial={{ x: 420 }} animate={{ x: 0 }} exit={{ x: 420 }} transition={{ type: "spring", damping: 28 }}
      className="fixed top-0 right-0 z-[90] h-screen w-full max-w-md bg-white border-l border-[#EAE6DF] flex flex-col shadow-2xl">
      <div className="p-4 border-b border-[#EAE6DF] flex items-center justify-between shrink-0">
        <span className="flex items-center gap-2 font-head font-semibold text-[#3D405B]"><MessageSquare size={18} className="text-[#E07A5F]" /> Messages</span>
        <button data-testid="messages-close" onClick={onClose} className="text-[#6B7280] hover:text-[#3D405B]"><X size={20} /></button>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        {view === "list" && (
          <>
            <div className="p-3">
              <button data-testid="new-message-btn" onClick={() => setView("new")}
                className="w-full flex items-center justify-center gap-2 rounded-full bg-[#3D405B] text-white py-2.5 text-sm font-semibold hover:bg-[#2c2f45] transition-colors"><Plus size={16} /> New message</button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3 space-y-2">
              {loading ? <p className="text-[#6B7280] text-sm p-3">Loading…</p> : convs.length === 0 ? (
                <p className="text-[#6B7280] text-sm p-3" data-testid="no-conversations">No conversations yet. Start one above!</p>
              ) : convs.map((c) => (
                <button key={c.id} data-testid={`conv-${c.id}`} onClick={() => { setConvId(c.id); setView("thread"); }}
                  className="w-full text-left rounded-xl border border-[#EAE6DF] px-4 py-3 hover:border-[#E07A5F]/50 transition-colors">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-[#3D405B] truncate flex items-center gap-1.5">
                      {c.type === "group" ? <Users size={14} /> : <MessageSquare size={14} />}{c.name}
                      {c.oversight && <span className="text-[10px] text-[#F4A261] flex items-center gap-0.5"><ShieldAlert size={11} /> oversight</span>}
                    </span>
                    {c.unread > 0 && <span className="stat text-xs bg-[#E07A5F] text-white rounded-full px-2 py-0.5">{c.unread}</span>}
                  </div>
                  <p className="text-xs text-[#6B7280] truncate mt-0.5">{c.last_message || "No messages yet"}</p>
                </button>
              ))}
            </div>
          </>
        )}
        {view === "new" && <NewChat onBack={() => setView("list")} onCreated={(id) => { setConvId(id); setView("thread"); loadList(); }} />}
        {view === "thread" && convId && <Thread convId={convId} onBack={() => { setView("list"); loadList(); }} onActivity={loadList} />}
      </div>

      <div className="px-4 py-2.5 border-t border-[#EAE6DF] bg-[#F7F5F0] text-[11px] text-[#6B7280] flex items-center gap-1.5 shrink-0">
        <ShieldAlert size={13} className="text-[#F4A261] shrink-0" /> Parents &amp; teachers have access to chat records.
      </div>
    </motion.div>
  );
}
